import React from 'react'
import {
  selectCameraStreamByPeerID,
  selectScreenShareByPeerID,
  useHMSStore,
  useVideo,
} from '@100mslive/react-sdk'
import {
  createActorParticipantId,
  createUserParticipantId,
  resolveParticipantId,
} from '../../domain'
import {
  PRESENCE_EVENT_TYPES,
  RTC_SESSION_EVENT_TYPES,
} from '../../ports'
import { RTC_PROVIDERS } from '../../providerIds'
import { isHumanParticipantIdentityKeyPrefix } from './participantIdentityContract'
import { createHundredMsRtcSessionHandle } from './createHundredMsRtcSessionHandle'

function createEventBus() {
  const listeners = new Set()

  return {
    emit(event) {
      listeners.forEach((listener) => {
        listener(event)
      })
    },
    subscribe(listener) {
      listeners.add(listener)

      return () => {
        listeners.delete(listener)
      }
    },
    clear() {
      listeners.clear()
    },
  }
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizePeerId(value) {
  return normalizeOptionalString(value, '')
}

function normalizeLocalParticipantIdentity(localParticipantIdentity = {}) {
  const participantId = resolveParticipantId(localParticipantIdentity)

  if (!participantId) {
    return null
  }

  return {
    participantId,
    userId: normalizeOptionalString(localParticipantIdentity?.userId),
    actorType: normalizeOptionalString(localParticipantIdentity?.actorType),
    actorId: normalizeOptionalString(
      localParticipantIdentity?.actorId ?? localParticipantIdentity?.stableActorId,
    ),
    displayName: normalizeOptionalString(localParticipantIdentity?.displayName),
    profilePicId: normalizeOptionalString(
      localParticipantIdentity?.profilePicId ?? localParticipantIdentity?.avatarImageId,
    ),
    role: normalizeOptionalString(localParticipantIdentity?.role),
  }
}

function parsePeerMetadata(metadata) {
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata
  }

  const normalizedMetadata = normalizeOptionalString(metadata)
  if (!normalizedMetadata) {
    return {}
  }

  try {
    const parsedMetadata = JSON.parse(normalizedMetadata)
    if (parsedMetadata && typeof parsedMetadata === 'object' && !Array.isArray(parsedMetadata)) {
      return parsedMetadata
    }
  } catch (error) {
    return {
      rawMetadata: normalizedMetadata,
    }
  }

  return {
    rawMetadata: normalizedMetadata,
  }
}

function parseIdentityKey(identityKey) {
  const normalizedIdentityKey = normalizeOptionalString(identityKey)

  if (!normalizedIdentityKey || !normalizedIdentityKey.includes(':')) {
    return null
  }

  const [rawPrefix, ...rawStableIdParts] = normalizedIdentityKey.split(':')
  const prefix = normalizeOptionalString(rawPrefix)
  const stableId = normalizeOptionalString(rawStableIdParts.join(':'))

  if (!prefix || !stableId) {
    return null
  }

  return {
    prefix,
    stableId,
  }
}

function createHundredMsTransport(peerId, metadata = {}, extraTransport = {}) {
  return {
    providerId: RTC_PROVIDERS.hundredMs,
    peerId,
    rawPeerMetadata:
      typeof metadata?.rawMetadata === 'string'
        ? metadata.rawMetadata
        : normalizeOptionalString(metadata?.rawPeerMetadata, '') || '',
    identityKey: normalizeOptionalString(metadata?.identityKey),
    profilePicId: normalizeOptionalString(
      metadata?.profilePicId ?? metadata?.avatarImageId,
    ),
    ...extraTransport,
  }
}

function deriveParticipantIdentity({
  peerId,
  metadata = {},
  isLocal = false,
  localParticipantIdentity = null,
  allowTransportFallback = true,
}) {
  const normalizedLocalIdentity = normalizeLocalParticipantIdentity(localParticipantIdentity)

  if (isLocal && normalizedLocalIdentity) {
    return normalizedLocalIdentity
  }

  const resolvedMetadataParticipantId = resolveParticipantId(metadata)
  if (resolvedMetadataParticipantId) {
    return {
      participantId: resolvedMetadataParticipantId,
      userId: normalizeOptionalString(metadata?.userId),
      actorType: normalizeOptionalString(metadata?.actorType),
      actorId: normalizeOptionalString(metadata?.actorId ?? metadata?.stableActorId),
      role: normalizeOptionalString(metadata?.role),
      displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
      profilePicId: normalizeOptionalString(
        metadata?.profilePicId ?? metadata?.avatarImageId,
      ),
    }
  }

  const parsedIdentityKey = parseIdentityKey(metadata?.identityKey)
  if (parsedIdentityKey) {
    if (isHumanParticipantIdentityKeyPrefix(parsedIdentityKey.prefix)) {
      return {
        participantId: createUserParticipantId(parsedIdentityKey.stableId),
        userId: parsedIdentityKey.stableId,
        actorType: 'user',
        actorId: parsedIdentityKey.stableId,
        role: normalizeOptionalString(metadata?.role, parsedIdentityKey.prefix),
        displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
        profilePicId: normalizeOptionalString(
          metadata?.profilePicId ?? metadata?.avatarImageId,
        ),
      }
    }

    return {
      participantId: createActorParticipantId(
        parsedIdentityKey.prefix,
        parsedIdentityKey.stableId,
      ),
      actorType: parsedIdentityKey.prefix,
      actorId: parsedIdentityKey.stableId,
      role: normalizeOptionalString(metadata?.role),
      displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
      profilePicId: normalizeOptionalString(
        metadata?.profilePicId ?? metadata?.avatarImageId,
      ),
    }
  }

  if (!allowTransportFallback || !peerId) {
    return null
  }

  return {
    participantId: createActorParticipantId('100ms-peer', peerId),
    actorType: '100ms-peer',
    actorId: peerId,
    role: normalizeOptionalString(metadata?.role, 'participant'),
    displayName: normalizeOptionalString(metadata?.displayName ?? metadata?.name),
    profilePicId: normalizeOptionalString(
      metadata?.profilePicId ?? metadata?.avatarImageId,
    ),
    transportIdentityFallback: true,
  }
}

function normalizeSnapshotParticipant(snapshotParticipant = {}, options = {}) {
  const peerId = normalizePeerId(snapshotParticipant?.peerId ?? snapshotParticipant?.id)
  if (!peerId) {
    return null
  }

  const parsedMetadata = parsePeerMetadata(snapshotParticipant?.metadata)
  const parsedIdentityKey = parseIdentityKey(parsedMetadata?.identityKey)
  const metadata = {
    ...parsedMetadata,
    role:
      normalizeOptionalString(parsedMetadata?.role) ||
      normalizeOptionalString(snapshotParticipant?.role) ||
      (parsedIdentityKey && isHumanParticipantIdentityKeyPrefix(parsedIdentityKey.prefix)
        ? null
        : normalizeOptionalString(snapshotParticipant?.roleName)),
    displayName:
      normalizeOptionalString(parsedMetadata?.displayName) ||
      normalizeOptionalString(snapshotParticipant?.displayName ?? snapshotParticipant?.name),
  }
  const identity = deriveParticipantIdentity({
    peerId,
    metadata,
    isLocal: options?.isLocal === true,
    localParticipantIdentity: options?.localParticipantIdentity,
    allowTransportFallback: options?.allowTransportFallback !== false,
  })

  if (!identity?.participantId) {
    return null
  }

  const isAudioEnabled =
    typeof snapshotParticipant?.isAudioEnabled === 'boolean'
      ? snapshotParticipant.isAudioEnabled
      : Boolean(snapshotParticipant?.audioTrackId ?? snapshotParticipant?.audioTrack)
  const isVideoEnabled =
    typeof snapshotParticipant?.isVideoEnabled === 'boolean'
      ? snapshotParticipant.isVideoEnabled
      : Boolean(snapshotParticipant?.videoTrackId ?? snapshotParticipant?.videoTrack)
  const screenSharing = snapshotParticipant?.screenSharing === true

  return {
    participantId: identity.participantId,
    userId: identity?.userId || null,
    actorType: identity?.actorType || null,
    actorId: identity?.actorId || null,
    displayName:
      identity?.displayName ||
      normalizeOptionalString(snapshotParticipant?.displayName ?? snapshotParticipant?.name) ||
      `Participant ${peerId}`,
    profilePicId:
      identity?.profilePicId ||
      normalizeOptionalString(metadata?.profilePicId ?? metadata?.avatarImageId),
    role:
      identity?.role ||
      normalizeOptionalString(snapshotParticipant?.role ?? snapshotParticipant?.roleName) ||
      'participant',
    isLocal: options?.isLocal === true,
    connectionState: normalizeOptionalString(
      snapshotParticipant?.connectionState,
      'connected',
    ),
    audioMuted: !isAudioEnabled,
    videoMuted: !isVideoEnabled,
    screenSharing,
    videoHandle: screenSharing
      ? { providerId: RTC_PROVIDERS.hundredMs, peerId, source: 'screen' }
      : isVideoEnabled
        ? { providerId: RTC_PROVIDERS.hundredMs, peerId, source: 'camera' }
        : null,
    renderKey: `100ms:${peerId}:${screenSharing ? 'screen' : 'camera'}`,
    transport: createHundredMsTransport(peerId, metadata, {
      roleName: normalizeOptionalString(snapshotParticipant?.roleName ?? snapshotParticipant?.role),
      transportIdentityFallback: identity?.transportIdentityFallback === true,
    }),
  }
}

function createHundredMsVideoBinderPort() {
  function AttachmentComponent({ participant, videoHandle, className = '' }) {
    const peerId = normalizePeerId(
      videoHandle?.peerId ?? participant?.transport?.peerId,
    )
    const isScreenShare =
      videoHandle?.source === 'screen' || participant?.screenSharing === true
    const videoTrack = useHMSStore(
      isScreenShare ? selectScreenShareByPeerID(peerId) : selectCameraStreamByPeerID(peerId),
    )
    const { videoRef } = useVideo({
      trackId: videoTrack?.id,
      attach: Boolean(videoTrack?.enabled !== false),
    })

    return React.createElement(
      'div',
      { className },
      React.createElement('video', {
        ref: videoRef,
        autoPlay: true,
        muted: Boolean(participant?.isLocal),
        playsInline: true,
        style: {
          width: '100%',
          height: '100%',
          objectFit: isScreenShare ? 'contain' : 'cover',
          background: '#020617',
        },
      }),
    )
  }

  return {
    AttachmentComponent,
    getRenderKey(participant = {}) {
      return (
        normalizeOptionalString(participant?.renderKey) ||
        normalizeOptionalString(participant?.id) ||
        normalizeOptionalString(participant?.participantId) ||
        normalizeOptionalString(participant?.transport?.peerId) ||
        '100ms:participant'
      )
    },
    async bindVideo() {
      return undefined
    },
    async unbindVideo() {
      return undefined
    },
  }
}

export function createHundredMsRtcPortBundle({
  initialConfig = {},
  sessionHandle = null,
  localParticipantIdentity = null,
} = {}) {
  const ownsSessionHandle = !sessionHandle
  const resolvedSessionHandle =
    sessionHandle || createHundredMsRtcSessionHandle({ initialConfig })
  const rtcEvents = createEventBus()
  const presenceEvents = createEventBus()

  let currentLocalParticipantIdentity = localParticipantIdentity || null

  function syncScreenShareConfigState(active) {
    const currentConfig =
      typeof resolvedSessionHandle?.getConfig === 'function'
        ? resolvedSessionHandle.getConfig()
        : null
    const currentEnabled = currentConfig?.rtcProps?.enableScreensharing === true

    if (active === currentEnabled) {
      return Promise.resolve(active)
    }

    return Promise.resolve(
      active === true
        ? resolvedSessionHandle.startScreenshare()
        : resolvedSessionHandle.stopScreenshare(),
    )
  }

  function emitParticipantSnapshot(snapshot) {
    const snapshotParticipants = Array.isArray(snapshot)
      ? snapshot
      : Array.isArray(snapshot?.participants)
        ? snapshot.participants
        : []

    const participants = snapshotParticipants
      .map((participant) =>
        normalizeSnapshotParticipant(participant, {
          isLocal: participant?.isLocal === true,
          localParticipantIdentity: currentLocalParticipantIdentity,
        }),
      )
      .filter(Boolean)

    rtcEvents.emit({
      type: RTC_SESSION_EVENT_TYPES.participantsChanged,
      participants,
    })
  }

  const rtcSession = {
    async join(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return resolvedSessionHandle.join()
    },
    async leave() {
      return resolvedSessionHandle.leave()
    },
    async setLocalAudioEnabled(enabled) {
      return resolvedSessionHandle.setAudioEnabled(enabled)
    },
    async setLocalVideoEnabled(enabled) {
      return resolvedSessionHandle.setVideoEnabled(enabled)
    },
    async setDevices(devices = {}) {
      const deviceTasks = []

      if (devices?.cameraDeviceId !== undefined) {
        deviceTasks.push(resolvedSessionHandle.switchCamera(devices.cameraDeviceId))
      }

      if (devices?.microphoneDeviceId !== undefined) {
        deviceTasks.push(
          resolvedSessionHandle.switchMicrophone(devices.microphoneDeviceId),
        )
      }

      if (devices?.playbackDeviceId !== undefined) {
        deviceTasks.push(
          resolvedSessionHandle.setPlaybackDevice(devices.playbackDeviceId),
        )
      }

      await Promise.all(deviceTasks)
      return true
    },
    async startScreenShare() {
      return resolvedSessionHandle.startScreenshare()
    },
    async stopScreenShare() {
      return resolvedSessionHandle.stopScreenshare()
    },
    subscribe(listener) {
      return rtcEvents.subscribe(listener)
    },
  }

  const presence = {
    async connect(config = {}) {
      if (config && Object.keys(config).length > 0) {
        resolvedSessionHandle.updateConfig(config)
      }

      return true
    },
    async disconnect() {
      return true
    },
    async publishLocalParticipantMetadata(metadata = {}) {
      currentLocalParticipantIdentity = {
        ...(currentLocalParticipantIdentity || {}),
        ...metadata,
      }

      const localParticipant = normalizeLocalParticipantIdentity(
        currentLocalParticipantIdentity,
      )

      if (localParticipant) {
        presenceEvents.emit({
          type: PRESENCE_EVENT_TYPES.participantMetadataChanged,
          participant: {
            ...localParticipant,
            isLocal: true,
          },
          metadata: {
            ...localParticipant,
            isLocal: true,
          },
          admitToRoster: false,
        })
      }

      return localParticipant
    },
    subscribe(listener) {
      return presenceEvents.subscribe(listener)
    },
  }

  const compatibilityCallbacks = {
    'participants-updated': (snapshot) => {
      emitParticipantSnapshot(snapshot)
    },
    'screen-share-state-changed': (active) => {
      syncScreenShareConfigState(active === true).catch(() => {})
    },
    'connection-state-change': (sessionState, previousState, reason) => {
      rtcEvents.emit({
        type: RTC_SESSION_EVENT_TYPES.stateChanged,
        sessionState,
        previousState,
        reason,
      })
    },
  }

  return {
    sessionHandle: resolvedSessionHandle,
    rtcSession,
    presence,
    videoBinder: createHundredMsVideoBinderPort(),
    compatibilityCallbacks,
    async destroy() {
      rtcEvents.clear()
      presenceEvents.clear()

      if (ownsSessionHandle) {
        await resolvedSessionHandle.destroy()
      }
    },
  }
}
